import React, { useEffect, useRef, useState } from 'react';
import './SafetyConcernsForm.css';

const SafetyConcernsForm = ({ 
  formData = {}, 
  onFormSubmit, 
  onFormChange,
  incidentId,
  loading = false,
  showSubmitButton = true
}) => {
  const buildSafetyData = (source = {}) => ({
    issueDescription: source.issueDescription || '',
    isRecurring: source.isRecurring || 'No',
    previousIssueDetails: source.previousIssueDetails || '',
    additionalNotifications: source.additionalNotifications || 'No',
    selectedManager: source.selectedManager || null,
    selectedCommitteeMember: source.selectedCommitteeMember || null,
    isAnonymous: !!source.isAnonymous
  });

  const areSafetyDataEqual = (left = {}, right = {}) => {
    return (
      (left.issueDescription || '') === (right.issueDescription || '') &&
      (left.isRecurring || 'No') === (right.isRecurring || 'No') &&
      (left.previousIssueDetails || '') === (right.previousIssueDetails || '') &&
      (left.additionalNotifications || 'No') === (right.additionalNotifications || 'No') &&
      (left.selectedManager || null) === (right.selectedManager || null) &&
      (left.selectedCommitteeMember || null) === (right.selectedCommitteeMember || null) &&
      !!left.isAnonymous === !!right.isAnonymous
    );
  };

  const buildOutgoingFormData = (source) => ({
    ...source,
    selectedManager: source.selectedManager || null,
    selectedCommitteeMember: source.selectedCommitteeMember || null,
    previousIssueDetails: source.previousIssueDetails || null,
    photos: null,
    isAnonymous: !!source.isAnonymous,
    completed: false
  });

  // Local state for this form's specific fields
  const [safetyData, setSafetyData] = useState(buildSafetyData(formData));
  const onFormChangeRef = useRef(onFormChange);
  // Health & Safety Committee Members
  const managers = [
    { id: 'dennisl@diamondvalley.town', name: 'Dennis Leis - Utility Operator Co-Chair', role: 'Committee Worker Member, Utilities' },
    { id: 'irisj@diamondvalley.town', name: 'Iris Jackson - Payroll & Benefits', role: 'Committee Worker Member, Finance' },
    { id: 'wyattr@diamondvalley.town', name: 'Wyatt Robinson - Parks & Facilities', role: 'Committee Worker Member, Parks' },
    { id: 'lauried@diamondvalley.town', name: 'Laurie Duquette - Arena Operator / Recreation Co-Chair', role: 'Committee Employer Member, Recreation' },
    { id: 'grants@diamondvalley.town', name: 'Grant Spicer - Deputy Fire Chief', role: 'Committee Employer Member, Fire Department, Station #3' },
    { id: 'thresial@diamondvalley.town', name: 'Thresia Lawhead - Finance Secretary', role: 'Committee Worker Member, Accounts Payable Clerk' },
    { id: 'kimh@diamondvalley.town', name: 'Kim Hampton - Roads', role: 'Committee Worker Member, Roads' },
    { id: 'trevord@diamondvalley.town', name: 'Trevor Doublet - Deputy Fire Chief', role: 'Committee Employer Member, Fire Department, Station #2' },
    { id: 'jimo@diamondvalley.town', name: 'Jim Olien - Firefighter', role: 'Committee Worker Member, Fire Department' },
  ];

  useEffect(() => {
    onFormChangeRef.current = onFormChange;
  }, [onFormChange]);

  useEffect(() => {
    const nextSafetyData = buildSafetyData(formData);
    setSafetyData((currentSafetyData) => (
      areSafetyDataEqual(currentSafetyData, nextSafetyData)
        ? currentSafetyData
        : nextSafetyData
    ));
  }, [formData]);

  useEffect(() => {
    if (typeof onFormChangeRef.current !== 'function') return;
    onFormChangeRef.current(buildOutgoingFormData(safetyData));
  }, [safetyData]);

  // Handle local form changes
  const handleLocalChange = (e) => {
    const { name, value } = e.target;

    setSafetyData((currentSafetyData) => ({
      ...currentSafetyData,
      [name]: value === '' ? null : value
    }));
  };

  // Submit the form data to parent with proper null handling
  const handleSubmit = (e) => {
    e.preventDefault();      // Clean the data before submission to ensure no undefined values
    const cleanedData = buildOutgoingFormData(safetyData);
    
    onFormSubmit(cleanedData, incidentId);
  };

  const FormContainer = showSubmitButton ? 'form' : 'div';
  const formContainerProps = showSubmitButton ? { onSubmit: handleSubmit } : {};

  return (    <FormContainer {...formContainerProps}>
      <h3 className="text-lg font-medium mb-4">Health & Safety Concerns Details</h3>
      
      <div className="mb-4">
        <label className="safety-checkbox-option">
          <input
            type="checkbox"
            name="isAnonymous"
            checked={safetyData.isAnonymous}
            onChange={(e) => setSafetyData((currentSafetyData) => ({
              ...currentSafetyData,
              isAnonymous: e.target.checked,
              additionalNotifications: e.target.checked ? 'No' : currentSafetyData.additionalNotifications,
              selectedManager: e.target.checked ? null : currentSafetyData.selectedManager
            }))}
            className="safety-checkbox-input"
          />
          <span className="text-gray-700">Submit this report anonymously</span>
        </label>        {safetyData.isAnonymous && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Anonymous Reporting:</strong> Your identity will not be disclosed. 
              No follow-up notifications will be sent to you regarding this report.
            </p>
          </div>
        )}
      </div>

      {/* Committee Member Copy - Available for all reports */}
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">
          Send Copy to Health & Safety Committee Member (Optional)
        </label>
        <select
          name="selectedCommitteeMember"
          value={safetyData.selectedCommitteeMember || ''}
          onChange={handleLocalChange}
          className="w-full border rounded-md px-3 py-2"
        >
          <option value="">No additional committee member</option>
          {managers.map((manager) => (
            <option key={manager.id} value={manager.id}>
              {manager.name} ({manager.role})
            </option>
          ))}
        </select>
        <p className="text-sm text-gray-600 mt-1">
          {safetyData.isAnonymous 
            ? "The selected committee member will receive a copy of the anonymous report along with the Health & Safety Advisor."
            : "The selected committee member will receive a copy of the report in addition to the Health & Safety Advisor."
          }
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Description of the Issue</label>
        <textarea
          name="issueDescription"
          value={safetyData.issueDescription || ''}
          onChange={handleLocalChange}
          className="w-full border rounded-md px-3 py-2 h-32"
          required
          placeholder="Describe the safety concern or issue in detail"
        ></textarea>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-1">Recurring?</label>
        <div className="safety-radio-group">
          <label className="safety-radio-option">
            <input
              type="radio"
              name="isRecurring"
              value="Yes"
              checked={safetyData.isRecurring === 'Yes'}
              onChange={handleLocalChange}
              className="safety-radio-input"
            />
            <span>Yes</span>
          </label>
          <label className="safety-radio-option">
            <input
              type="radio"
              name="isRecurring"
              value="No"
              checked={safetyData.isRecurring === 'No'}
              onChange={handleLocalChange}
              className="safety-radio-input"
            />
            <span>No</span>
          </label>
        </div>
      </div>
      
      {safetyData.isRecurring === 'Yes' && (
        <div className="mb-4">
          <label className="block text-gray-700 mb-1">Previous Issue Details</label>
          <textarea
            name="previousIssueDetails"
            value={safetyData.previousIssueDetails || ''}
            onChange={handleLocalChange}
            className="w-full border rounded-md px-3 py-2 h-24"
            required
            placeholder="Describe the previous issue / concern"
          ></textarea>
        </div>
      )}
        <div className="mb-4">
        <label className="block text-gray-700 mb-1">
          Additional Notifications 
          {safetyData.isAnonymous && <span className="text-sm text-gray-500">(Not available for anonymous reports)</span>}
        </label>
        <div className="safety-radio-group">
          <label className="safety-radio-option">
            <input
              type="radio"
              name="additionalNotifications"
              value="Yes"
              checked={safetyData.additionalNotifications === 'Yes'}
              onChange={handleLocalChange}
              disabled={safetyData.isAnonymous}
              className="safety-radio-input"
            />
            <span>Yes</span>
          </label>
          <label className="safety-radio-option">
            <input
              type="radio"
              name="additionalNotifications"
              value="No"
              checked={safetyData.additionalNotifications === 'No'}
              onChange={handleLocalChange}
              disabled={safetyData.isAnonymous}
              className="safety-radio-input"
            />
            <span>No</span>
          </label>
        </div>
      </div>
        {safetyData.additionalNotifications === 'Yes' && !safetyData.isAnonymous && (
        <div className="mb-4">
          <label className="block text-gray-700 mb-1">Health & Safety Committee Members</label>
          <select
            name="selectedManager"
            value={safetyData.selectedManager || ''}
            onChange={handleLocalChange}
            className="w-full border rounded-md px-3 py-2"
            required
          >
            <option value="">Select a committee member...</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.name} ({manager.role})
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-600 mt-1">
            The selected committee member will receive an additional notification about this safety concern.
          </p>
        </div>
      )}
      
      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-sm text-gray-700">
          Use the attachments section below the main incident form to add photos or files for this report.
        </p>
      </div>
        {(safetyData.additionalNotifications === 'No' || safetyData.isAnonymous) && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            {safetyData.isAnonymous 
              ? "The anonymous report will be sent to safety@diamondvalley.town for review."
              : "The report will be automatically sent to the Health & Safety Advisor."
            }
          </p>
        </div>
      )}
      
      {showSubmitButton && (
        <div className="flex justify-end">
          <button 
            type="submit" 
            disabled={loading}
            className={loading ? 'submit-button disabled' : 'submit-button'}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}
    </FormContainer>
  );
};

export default SafetyConcernsForm;